'use client'

import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import PublicIcon from '@mui/icons-material/Public'
import TranslateIcon from '@mui/icons-material/Translate'
import { Box, Button, Card, CardActionArea, CardContent, Divider, Link, Stack, Typography } from '@mui/material'
import React from 'react'

import { AccountSettingsItem } from '@/src/types/account'
import BookingsIcon from '@/src/assets/svgs/Bookings'
import WalletIcon from '@/src/assets/svgs/Wallet'

export type AccountMenuItemId = 'bookings' | 'wallet' | 'help' | 'profile' | 'travellers' | 'signOut' | 'privacy' | 'terms'

export interface AccountMenuItem {
    id: AccountMenuItemId
    label: string
    onClick: () => void
}

interface AccountPageProps {
    isLoggedIn: boolean
    labels: {
        hello: string
        yourProfile: string
        manageYourAccount: string
        logInToUnlock: string
        logInSignUp: string
        safeSecure: string
        version: string
    }
    menuItems: AccountMenuItem[]
    settingsItems: AccountSettingsItem[]
    appVersion: string
    hideSignIn?: boolean
    onModalOpen?: (modal: 'signIn') => void
    username?: string
    greetingName?: string
}

const AccountPage: React.FC<AccountPageProps> = ({
    isLoggedIn,
    labels,
    menuItems,
    settingsItems,
    appVersion,
    hideSignIn = false,
    onModalOpen,
    username,
    greetingName = 'User'
}) => {
    const settingsIconMap: Record<AccountSettingsItem['id'], React.ReactElement> = {
        country: <PublicIcon sx={{ fontSize: 20 }} />,
        currency: <CurrencyExchangeIcon sx={{ fontSize: 20 }} />,
        language: <TranslateIcon sx={{ fontSize: 20 }} />
    }

    const groupedCardSx = {
        mx: 2,
        mt: 1.5,
        borderRadius: '12px',
        border: 1,
        borderColor: 'divider',
        boxShadow: 'none',
        bgcolor: 'background.paper'
    }

    const rowSx = {
        px: 2,
        py: 2.5,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        cursor: 'pointer'
    }

    const getMenuItem = (id: AccountMenuItemId) => menuItems.find(item => item.id === id)
    const bookingsItem = getMenuItem('bookings')
    const walletItem = getMenuItem('wallet')
    const helpItem = getMenuItem('help')
    const profileItem = getMenuItem('profile')
    const travellersItem = getMenuItem('travellers')
    const signOutItem = getMenuItem('signOut')
    const privacyItem = getMenuItem('privacy')
    const termsItem = getMenuItem('terms')

    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: 'background.default',
                pb: 4
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    px: 3,
                    py: 2,
                    bgcolor: 'background.paper',
                    boxShadow: '2px 4px 6px rgba(0, 0, 0, 0.05)'
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '20px', mb: 0.5 }}>
                    {isLoggedIn ? `${labels.hello}, ${greetingName}!` : labels.yourProfile}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '14px', mb: 2 }}>
                    {isLoggedIn ? labels.manageYourAccount : labels.logInToUnlock}
                </Typography>

                {!isLoggedIn && !hideSignIn && onModalOpen && (
                    <Button
                        variant="customVariantPrimary"
                        fullWidth
                        size="medium"
                        onClick={() => onModalOpen('signIn')}
                        sx={{
                            py: 1,
                            fontSize: '14px',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 0.5
                        }}
                    >
                        {labels.logInSignUp}
                    </Button>
                )}
            </Box>

            {/* Bookings + Wallet */}
            <Box sx={{ mt: 1.5, px: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <Card elevation={0} sx={{ borderRadius: '12px', border: 1, borderColor: 'divider' }}>
                    <CardActionArea onClick={bookingsItem?.onClick} sx={{ height: '100%' }} disabled={!bookingsItem}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ color: 'text.secondary', mb: 2 }}>
                                <BookingsIcon sx={{ fontSize: 30 }} />
                            </Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary' }}>
                                {bookingsItem?.label}
                            </Typography>
                        </CardContent>
                    </CardActionArea>
                </Card>

                <Card elevation={0} sx={{ borderRadius: '12px', border: 1, borderColor: 'divider' }}>
                    <CardActionArea onClick={walletItem?.onClick} sx={{ height: '100%' }} disabled={!walletItem}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ color: 'text.secondary', mb: 2 }}>
                                <WalletIcon sx={{ fontSize: 30 }} />
                            </Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary' }}>
                                {walletItem?.label}
                            </Typography>
                        </CardContent>
                    </CardActionArea>
                </Card>
            </Box>

            {/* Help & Support */}
            <Card elevation={0} sx={groupedCardSx}>
                <CardActionArea onClick={helpItem?.onClick} disabled={!helpItem}>
                    <Box sx={rowSx}>
                        <Box sx={{ color: 'text.secondary', display: 'inline-flex' }}>
                            <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary', flex: 1 }}>
                            {helpItem?.label}
                        </Typography>
                        <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </Box>
                </CardActionArea>
            </Card>

            {/* Profile + Travellers */}
            <Card elevation={0} sx={groupedCardSx}>
                <CardActionArea onClick={profileItem?.onClick} disabled={!profileItem}>
                    <Box sx={rowSx}>
                        <Box sx={{ color: 'text.secondary', display: 'inline-flex' }}>
                            <PersonOutlineRoundedIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary', flex: 1 }}>
                            {profileItem?.label}
                        </Typography>
                        <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </Box>
                </CardActionArea>
                <Divider sx={{ mx: 2 }} />
                <CardActionArea onClick={travellersItem?.onClick} disabled={!travellersItem}>
                    <Box sx={rowSx}>
                        <Box sx={{ color: 'text.secondary', display: 'inline-flex' }}>
                            <GroupOutlinedIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary', flex: 1 }}>
                            {travellersItem?.label}
                        </Typography>
                        <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </Box>
                </CardActionArea>
            </Card>

            {/* Country / Currency / Language */}
            <Card elevation={0} sx={groupedCardSx}>
                {settingsItems.map((item, index) => (
                    <React.Fragment key={item.id}>
                        <CardActionArea onClick={item.onClick}>
                            <Box sx={rowSx}>
                                <Box sx={{ color: 'text.secondary', display: 'inline-flex' }}>{settingsIconMap[item.id]}</Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary', flex: 1 }}>{item.label}</Typography>
                                {item.rightContent && (
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'text.secondary' }}>
                                        {item.rightContent}
                                    </Stack>
                                )}
                                <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                            </Box>
                        </CardActionArea>
                        {index < settingsItems.length - 1 && <Divider sx={{ mx: 2 }} />}
                    </React.Fragment>
                ))}
            </Card>

            {/* Sign Out Button - Only show when logged in */}
            {isLoggedIn && signOutItem && (
                <Card elevation={0} sx={groupedCardSx}>
                    <CardActionArea onClick={signOutItem.onClick}>
                        <Box sx={rowSx}>
                            <LogoutOutlinedIcon sx={{ fontSize: 20, color: 'error.main' }} />
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'error.main' }}>
                                {signOutItem.label}
                            </Typography>
                        </Box>
                    </CardActionArea>
                </Card>
            )}

            {/* Footer Links */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                    mt: 3,
                    px: 2
                }}
            >
                <Link
                    component="button"
                    onClick={privacyItem?.onClick}
                    disabled={!privacyItem}
                    sx={{
                        fontSize: '13px',
                        fontWeight: 500,
                        textDecoration: 'none',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' }
                    }}
                >
                    {privacyItem?.label}
                </Link>
                <Link
                    component="button"
                    onClick={termsItem?.onClick}
                    disabled={!termsItem}
                    sx={{
                        fontSize: '13px',
                        fontWeight: 500,
                        textDecoration: 'none',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' }
                    }}
                >
                    {termsItem?.label}
                </Link>
            </Box>

            {/* Security Message */}
            <Box
                sx={{
                    textAlign: 'center',
                    mt: 4,
                    mb: 2,
                    px: 2
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        fontSize: '13px',
                        mb: 0.5
                    }}
                >
                    {labels.safeSecure}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.disabled',
                        fontSize: '12px'
                    }}
                >
                    {labels.version} {appVersion}
                </Typography>
            </Box>
        </Box>
    )
}

export { AccountPage }
export type { AccountPageProps }
